import React from 'react'
import { Box, Button, TextField, Typography, Autocomplete, Checkbox, IconButton, CircularProgress } from "@mui/material";
import Modal from '@mui/material/Modal';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import 'mathlive';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import { useTheme } from "@mui/material";
import { tokens } from "../../theme";
import Axios from 'axios';
import { getAuthHeader } from '../../utils/authToken';
import { GRADING_TYPES, ANSWER_TOOLS } from '../../utils/hwConstants';
import TextFieldWithMath from '../hwQuestions/TextFieldWithMath';
import QuestionRender from '../../components/QuestionRender';
import { CoordinateGridAdmin, defaultCoordinateConfig } from '../../components/coordinateGrid';
import { TableAdmin, defaultTableConfig, extractAnswerFromConfig, createEmptyRow } from '../../components/tableAnswer';
import { NumberLineAdmin, defaultNumberLineConfig } from '../../components/numberLine';
import { MappingAdmin, defaultMappingConfig, parseAnswer as parseMappingAnswer, stringifyAnswer as stringifyMappingAnswer } from '../../components/mappingAnswer';
import { PolynomialGraphAdmin, defaultPolynomialGraphConfig } from '../../components/polynomialGraph';
import { RationalGraphAdmin, defaultRationalGraphConfig } from '../../components/rationalGraph';
import { TrigGraphAdmin, defaultTrigGraphConfig } from '../../components/trigGraph';
import { MultilineAdmin, defaultMultilineConfig } from '../../components/multiline';
import {
    defaultMathliveConfig,
    MATHLIVE_ANSWER_TYPES,
    parseMathliveAnswer,
    stringifyMathliveAnswer,
    wrapLatexDelimiters,
    hasLatexDelimiters,
    MathAnswerSpecEditor,
} from '../../components/mathlive';

const MathFieldInput = ({ value, onInput, style: mfStyle }) => {
    const ref = React.useRef(null);
    React.useEffect(() => {
        const el = ref.current;
        if (el && el.value !== (value ?? '')) {
            el.value = value ?? '';
        }
    }, [value]);
    return (
        <math-field
            ref={ref}
            onInput={onInput}
            style={mfStyle}
        />
    );
};

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 800,
    maxHeight: '90vh',
    overflow: 'auto',
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
};
  

// Decode persisted mathlive answers into parallel `values`/`types` arrays.
// Handles both the legacy plain-string shape and the new `{type,value}` JSON
// shape. The UI (and save-path) operate on plain `values`, with `types` only
// consulted when `answer_config.allowTextInput` is on.
const decodeMathliveAnswers = (raw, config) => {
    const arr = Array.isArray(raw) ? raw : (raw?.split('|') || []);
    if (!config || !config.allowTextInput) {
        return { values: arr, types: arr.map(() => MATHLIVE_ANSWER_TYPES.MATH) };
    }
    const parsed = arr.map(parseMathliveAnswer);
    return {
        values: parsed.map(p => p.value),
        types: parsed.map(p => p.type),
    };
};

const ExerciseEditModal = ({
    question,
    numPages,
    setUpdated,
    setSuccessAlertOpen,
    setSuccessAlertMessage,
    setErrorAlertOpen,
    setErrorAlertMessage,
}) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [open, setOpen] = React.useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const [questionLabel, setQuestionLabel] = React.useState(question.question_label || "");
    const [passage, setPassage] = React.useState(question.passage || "");
    const [content, setContent] = React.useState(question.content || "");
    const [chosenQuestionType, setChosenQuestionType] = React.useState(question.question_type || null);
    const [options, setOptions] = React.useState(
        Array.isArray(question.options) ? question.options : (question.options?.split("|") || [])
    );
    const initialMathliveConfig = question.answer_tool === 'mathlive' && question.answer_config
        ? question.answer_config
        : defaultMathliveConfig;
    const initialDecodedMathlive = question.answer_tool === 'mathlive'
        ? decodeMathliveAnswers(question.answer, initialMathliveConfig)
        : { values: null, types: [] };
    const [answers, setAnswers] = React.useState(
        initialDecodedMathlive.values
            ?? (Array.isArray(question.answer) ? question.answer : (question.answer?.split("|") || []))
    );
    const [chosenTool, setChosenTool] = React.useState(question.answer_tool || null);
    const [gradingType, setGradingType] = React.useState(question.grading_type || null);
    const [newPage, setNewPage] = React.useState(question.page || 1);
    const [numAnswers, setNumAnswers] = React.useState(
        Array.isArray(question.answer) ? question.answer.length : (question.answer?.split("|").length || 1)
    );
    const [showCustomAnswerInput, setShowCustomAnswerInput] = React.useState(false);
    const [customAnswerValue, setCustomAnswerValue] = React.useState("");

    // Coordinate grid state
    const [coordinateConfig, setCoordinateConfig] = React.useState(
        question.answer_config || defaultCoordinateConfig
    );
    const [coordinateAnswer, setCoordinateAnswer] = React.useState(() => {
        if (question.answer_tool === 'coordinate' && question.answer?.[0]) {
            try {
                return JSON.parse(question.answer[0]);
            } catch {
                return { elements: [] };
            }
        }
        return { elements: [] };
    });

    // Mapping state
    const [mappingConfig, setMappingConfig] = React.useState(
        question.answer_tool === 'mapping' && question.answer_config
            ? question.answer_config
            : defaultMappingConfig
    );
    const [mappingAnswer, setMappingAnswer] = React.useState(() => {
        if (question.answer_tool === 'mapping' && question.answer?.[0]) {
            return question.answer[0];
        }
        return stringifyMappingAnswer([]);
    });

    // Table state
    const [tableConfig, setTableConfig] = React.useState(
        question.answer_tool === 'table' && question.answer_config
            ? question.answer_config
            : defaultTableConfig
    );
    const [tableAnswer, setTableAnswer] = React.useState(() => {
        if (question.answer_tool === 'table' && question.answer?.[0]) {
            try { return JSON.parse(question.answer[0]); } catch { /* fall through */ }
        }
        // Mirror the legacy `extractAnswerFromConfig(tableConfig)` save derivation
        // as the initial answer, so an existing question loads with cells populated.
        return extractAnswerFromConfig(
            question.answer_tool === 'table' && question.answer_config
                ? question.answer_config
                : defaultTableConfig
        );
    });

    // Number Line state
    const [numberLineConfig, setNumberLineConfig] = React.useState(
        question.answer_tool === 'numberline' && question.answer_config
            ? question.answer_config
            : defaultNumberLineConfig
    );
    const [numberLineAnswer, setNumberLineAnswer] = React.useState(() => {
        if (question.answer_tool === 'numberline' && question.answer?.[0]) {
            try {
                return JSON.parse(question.answer[0]);
            } catch {
                return { elements: [] };
            }
        }
        return { elements: [] };
    });

    // Polynomial Graph state
    const [polynomialGraphConfig, setPolynomialGraphConfig] = React.useState(
        question.answer_tool === 'polynomialGraph' && question.answer_config
            ? question.answer_config
            : defaultPolynomialGraphConfig
    );
    const [polynomialGraphAnswer, setPolynomialGraphAnswer] = React.useState(() => {
        if (question.answer_tool === 'polynomialGraph' && question.answer?.[0]) {
            try {
                return JSON.parse(question.answer[0]);
            } catch {
                return { correctGraphIndex: 0 };
            }
        }
        return { correctGraphIndex: 0 };
    });

    // Rational Graph state
    const [rationalGraphConfig, setRationalGraphConfig] = React.useState(
        question.answer_tool === 'rationalGraph' && question.answer_config
            ? question.answer_config
            : defaultRationalGraphConfig
    );
    const [rationalGraphAnswer, setRationalGraphAnswer] = React.useState(() => {
        if (question.answer_tool === 'rationalGraph' && question.answer?.[0]) {
            try {
                return JSON.parse(question.answer[0]);
            } catch {
                return { correctGraphIndex: 0 };
            }
        }
        return { correctGraphIndex: 0 };
    });

    // Trig Graph state
    const [trigGraphConfig, setTrigGraphConfig] = React.useState(
        question.answer_tool === 'trigGraph' && question.answer_config
            ? question.answer_config
            : defaultTrigGraphConfig
    );
    const [trigGraphAnswer, setTrigGraphAnswer] = React.useState(() => {
        if (question.answer_tool === 'trigGraph' && question.answer?.[0]) {
            try { return JSON.parse(question.answer[0]); } catch { return { correctGraphIndex: 0 }; }
        }
        return { correctGraphIndex: 0 };
    });

    // Multiline state
    const [multilineConfig, setMultilineConfig] = React.useState(
        question.answer_tool === 'multiline' && question.answer_config
            ? question.answer_config
            : defaultMultilineConfig
    );
    const [multilineAnswer, setMultilineAnswer] = React.useState(() => {
        if (question.answer_tool === 'multiline' && question.answer?.[0]) {
            try { return JSON.parse(question.answer[0]); } catch { return { rows: [] }; }
        }
        return { rows: [] };
    });

    // Mathlive state
    const [mathliveConfig, setMathliveConfig] = React.useState(initialMathliveConfig);
    const [mathliveAnswerTypes, setMathliveAnswerTypes] = React.useState(initialDecodedMathlive.types);

    // Re-solve as coordinate state
    const [resolving, setResolving] = React.useState(false);
    // Bumped after a successful AI re-solve to remount the per-tool Admin
    // components — they initialize their internal state (parsed equations,
    // variations, localConfig) from props on mount only, so swapping props
    // alone doesn't refresh the displayed graph/answer.
    const [resolveKey, setResolveKey] = React.useState(0);

    // Render as LaTeX checkbox state - initialize based on existing answers having delimiters or mathlive tool
    const [renderAsLatex, setRenderAsLatex] = React.useState(() => {
        if (question.answer_tool === 'mathlive') return true;
        // Check if any answer already has LaTeX delimiters
        const answers = Array.isArray(question.answer) ? question.answer : (question.answer?.split("|") || []);
        return answers.some(ans => ans && /\\\(|\\\)|\$/.test(ans));
    });

    // Reset all form state when question changes
    React.useEffect(() => {
        setQuestionLabel(question.question_label || "");
        setPassage(question.passage || "");
        setContent(question.content || "");
        setChosenQuestionType(question.question_type || null);
        setOptions(
            Array.isArray(question.options) ? question.options : (question.options?.split("|") || [])
        );
        const nextMathliveConfig = question.answer_tool === 'mathlive' && question.answer_config
            ? question.answer_config
            : defaultMathliveConfig;
        const nextDecodedMathlive = question.answer_tool === 'mathlive'
            ? decodeMathliveAnswers(question.answer, nextMathliveConfig)
            : { values: null, types: [] };
        setAnswers(
            nextDecodedMathlive.values
                ?? (Array.isArray(question.answer) ? question.answer : (question.answer?.split("|") || []))
        );
        setMathliveConfig(nextMathliveConfig);
        setMathliveAnswerTypes(nextDecodedMathlive.types);
        setChosenTool(question.answer_tool || null);
        setGradingType(question.grading_type || null);
        setNewPage(question.page || 1);
        setNumAnswers(
            Array.isArray(question.answer) ? question.answer.length : (question.answer?.split("|").length || 1)
        );
        setShowCustomAnswerInput(false);
        setCustomAnswerValue("");
        setCoordinateConfig(question.answer_config || defaultCoordinateConfig);
        setCoordinateAnswer(() => {
            if (question.answer_tool === 'coordinate' && question.answer?.[0]) {
                try {
                    return JSON.parse(question.answer[0]);
                } catch {
                    return { elements: [] };
                }
            }
            return { elements: [] };
        });
        setMappingConfig(
            question.answer_tool === 'mapping' && question.answer_config
                ? question.answer_config
                : defaultMappingConfig
        );
        setMappingAnswer(() => {
            if (question.answer_tool === 'mapping' && question.answer?.[0]) {
                return question.answer[0];
            }
            return stringifyMappingAnswer([]);
        });
        setTableConfig(
            question.answer_tool === 'table' && question.answer_config
                ? question.answer_config
                : defaultTableConfig
        );
        setTableAnswer(() => {
            if (question.answer_tool === 'table' && question.answer?.[0]) {
                try { return JSON.parse(question.answer[0]); } catch { /* fall through */ }
            }
            return extractAnswerFromConfig(
                question.answer_tool === 'table' && question.answer_config
                    ? question.answer_config
                    : defaultTableConfig
            );
        });
        setNumberLineConfig(
            question.answer_tool === 'numberline' && question.answer_config
                ? question.answer_config
                : defaultNumberLineConfig
        );
        setNumberLineAnswer(() => {
            if (question.answer_tool === 'numberline' && question.answer?.[0]) {
                try {
                    return JSON.parse(question.answer[0]);
                } catch {
                    return { elements: [] };
                }
            }
            return { elements: [] };
        });
        setPolynomialGraphConfig(
            question.answer_tool === 'polynomialGraph' && question.answer_config
                ? question.answer_config
                : defaultPolynomialGraphConfig
        );
        setPolynomialGraphAnswer(() => {
            if (question.answer_tool === 'polynomialGraph' && question.answer?.[0]) {
                try {
                    return JSON.parse(question.answer[0]);
                } catch {
                    return { correctGraphIndex: 0 };
                }
            }
            return { correctGraphIndex: 0 };
        });
        setRationalGraphConfig(
            question.answer_tool === 'rationalGraph' && question.answer_config
                ? question.answer_config
                : defaultRationalGraphConfig
        );
        setRationalGraphAnswer(() => {
            if (question.answer_tool === 'rationalGraph' && question.answer?.[0]) {
                try {
                    return JSON.parse(question.answer[0]);
                } catch {
                    return { correctGraphIndex: 0 };
                }
            }
            return { correctGraphIndex: 0 };
        });
        setTrigGraphConfig(
            question.answer_tool === 'trigGraph' && question.answer_config
                ? question.answer_config
                : defaultTrigGraphConfig
        );
        setTrigGraphAnswer(() => {
            if (question.answer_tool === 'trigGraph' && question.answer?.[0]) {
                try {
                    return JSON.parse(question.answer[0]);
                } catch {
                    return { correctGraphIndex: 0 };
                }
            }
            return { correctGraphIndex: 0 };
        });
        setMultilineConfig(
            question.answer_tool === 'multiline' && question.answer_config
                ? question.answer_config
                : defaultMultilineConfig
        );
        setMultilineAnswer(() => {
            if (question.answer_tool === 'multiline' && question.answer?.[0]) {
                try { return JSON.parse(question.answer[0]); } catch { return { rows: [] }; }
            }
            return { rows: [] };
        });
        // Reset renderAsLatex based on existing data
        setRenderAsLatex(() => {
            if (question.answer_tool === 'mathlive') return true;
            const ans = Array.isArray(question.answer) ? question.answer : (question.answer?.split("|") || []);
            return ans.some(a => a && /\\\(|\\\)|\$/.test(a));
        });
    }, [question.id]);

    async function handleSave() {
        if (questionLabel === "") {
            setErrorAlertMessage("Question label is required!");
            setErrorAlertOpen(true);
            return;
        }

        if (!newPage || newPage < 1 || (numPages && newPage > numPages)) {
            setErrorAlertMessage(`Page must be between 1 and ${numPages || 'the total number of pages'}!`);
            setErrorAlertOpen(true);
            return;
        }

        if (chosenQuestionType === null) {
            setErrorAlertMessage("Question type is required!");
            setErrorAlertOpen(true);
            return;
        }

        if (chosenQuestionType === "multiple_choice" && options.length < 2) {
            setErrorAlertMessage("At least 2 options are required for multiple choice question!");
            setErrorAlertOpen(true);
            return;
        }

        // Skip answer validation for coordinate, mapping, table, numberline, and polynomialGraph tools (they handle their own answers)
        const skipAnswerValidation = chosenTool === 'coordinate' || chosenTool === 'mapping' || chosenTool === 'table' || chosenTool === 'numberline' || chosenTool === 'polynomialGraph' || chosenTool === 'rationalGraph' || chosenTool === 'trigGraph' || chosenTool === 'multiline' || chosenTool === 'tinymce';
        if ((chosenQuestionType === "multiple_choice" || chosenQuestionType === "short_answer") && answers.length === 0 && !skipAnswerValidation) {
            setErrorAlertMessage("Answer is required for this question type!");
            setErrorAlertOpen(true);
            return;
        }

        if ((chosenQuestionType === "short_answer" || chosenQuestionType === "long_answer")  && chosenTool === null) {
            setErrorAlertMessage("Answer tool is required for this question type!");
            setErrorAlertOpen(true);
            return;
        }

        for (let i = 0; i < answers.length; i++) {
            if (answers[i] === "" && chosenQuestionType !== "long_answer" && chosenQuestionType !== "file_upload" && !skipAnswerValidation) {
                setErrorAlertMessage("Answer cannot be empty!");
                setErrorAlertOpen(true);
                return;
            }
        }

        let answer = null;
        const isCoordinateTool = chosenTool === 'coordinate';
        const isMappingTool = chosenTool === 'mapping';
        const isTableTool = chosenTool === 'table';
        const isNumberLineTool = chosenTool === 'numberline';
        const isPolynomialGraphTool = chosenTool === 'polynomialGraph';
        const isRationalGraphTool = chosenTool === 'rationalGraph';
        const isTrigGraphTool = chosenTool === 'trigGraph';
        const isMultilineTool = chosenTool === 'multiline';
        const isMathliveTool = chosenTool === 'mathlive';
        const mathliveAllowsText = isMathliveTool && mathliveConfig.allowTextInput === true;
        if (chosenQuestionType !== "long_answer" && chosenQuestionType !== "file_upload") {
            if (isCoordinateTool) {
                // Strip hydrated points from curve elements — they are regenerated on load
                const cleanedCoordAnswer = {
                    ...coordinateAnswer,
                    elements: (coordinateAnswer?.elements || []).map(el =>
                        el.type === 'curve' && el.points ? { ...el, points: undefined } : el
                    ),
                };
                answer = [JSON.stringify(cleanedCoordAnswer)];
            } else if (isMappingTool) {
                answer = [mappingAnswer];
            } else if (isTableTool) {
                answer = [JSON.stringify(tableAnswer)];
            } else if (isNumberLineTool) {
                answer = [JSON.stringify(numberLineAnswer)];
            } else if (isPolynomialGraphTool) {
                answer = [JSON.stringify(polynomialGraphAnswer)];
            } else if (isRationalGraphTool) {
                answer = [JSON.stringify(rationalGraphAnswer)];
            } else if (isTrigGraphTool) {
                answer = [JSON.stringify(trigGraphAnswer)];
            } else if (isMultilineTool) {
                // Always persist admin's authored rows; for manual mode they
                // serve as the grader's reference answer.
                answer = [JSON.stringify(multilineAnswer)];
            } else if (mathliveAllowsText) {
                answer = answers.map((ans, i) => {
                    const type = mathliveAnswerTypes[i] === MATHLIVE_ANSWER_TYPES.TEXT
                        ? MATHLIVE_ANSWER_TYPES.TEXT
                        : MATHLIVE_ANSWER_TYPES.MATH;
                    const strAns = typeof ans === 'string' ? ans : String(ans ?? '');
                    const value = (type === MATHLIVE_ANSWER_TYPES.MATH && renderAsLatex)
                        ? wrapLatexDelimiters(strAns)
                        : strAns;
                    return stringifyMathliveAnswer({ type, value });
                });
            } else if (renderAsLatex && (chosenTool === 'mathlive' || chosenTool === 'textfield' || !chosenTool)) {
                // Wrap answers with \(...\) delimiters if "Render as LaTeX" is checked
                answer = answers.map(ans => {
                    if (!ans) return ans;
                    const strAns = typeof ans === 'string' ? ans : String(ans);
                    const trimmed = strAns.trim();
                    // Check if already has delimiters
                    if (/^\\\(.*\\\)$/.test(trimmed) || /^\$.*\$$/.test(trimmed) || /^\\\[.*\\\]$/.test(trimmed)) {
                        return strAns;
                    }
                    return `\\(${strAns}\\)`;
                });
            } else {
                answer = answers;
            }
        }

        // Determine answer_config based on tool type
        let answerConfig = null;
        if (isCoordinateTool) {
            answerConfig = coordinateConfig;
        } else if (isMappingTool) {
            answerConfig = mappingConfig;
        } else if (isTableTool) {
            answerConfig = tableConfig;
        } else if (isNumberLineTool) {
            answerConfig = numberLineConfig;
        } else if (isPolynomialGraphTool) {
            answerConfig = polynomialGraphConfig;
        } else if (isRationalGraphTool) {
            answerConfig = rationalGraphConfig;
        } else if (isTrigGraphTool) {
            answerConfig = trigGraphConfig;
        } else if (isMultilineTool) {
            answerConfig = multilineConfig;
        } else if (isMathliveTool) {
            answerConfig = mathliveConfig;
        }

        // save the question
        await Axios.put(`${process.env.REACT_APP_URL}/api/v1/hw-questions/${question.id}`, {
            question_label: questionLabel,
            passage: passage || null,
            content: content || null,
            question_type: chosenQuestionType,
            options: options || null,
            answer: answer || null,
            answer_tool: chosenTool || null,
            grading_type: chosenQuestionType === 'long_answer' ? gradingType : null,
            answer_config: answerConfig,
            page: newPage,
        }, {
            headers: {
                Authorization: getAuthHeader(),
            }
        }).then(() => {
            setSuccessAlertMessage("Question updated successfully!");
            setSuccessAlertOpen(true);
            setUpdated(true);
            handleClose();
        }).catch((error) => {
            console.log("error: ", error);
            setErrorAlertMessage(
                error.response?.data?.error
                || error.response?.data?.message
                || "Something went wrong"
            );
            setErrorAlertOpen(true);
        });
    }

    /**
     * Re-solve the current question using AI for the answer_tool the admin
     * already selected via the dropdown. Dispatches the response into the
     * matching per-tool state setter — never overwrites chosenTool.
     */
    async function handleResolveWithAI() {
        if (!content) {
            setErrorAlertMessage('Please enter question content first');
            setErrorAlertOpen(true);
            return;
        }
        if (!chosenTool) {
            setErrorAlertMessage('Pick an answer tool first');
            setErrorAlertOpen(true);
            return;
        }

        // Pass the config that matches the chosen tool so the AI gets schema hints
        const configByTool = {
            coordinate: coordinateConfig,
            mapping: mappingConfig,
            table: tableConfig,
            numberline: numberLineConfig,
            polynomialGraph: polynomialGraphConfig,
            rationalGraph: rationalGraphConfig,
            trigGraph: trigGraphConfig,
            multiline: multilineConfig,
            mathlive: mathliveConfig,
        };

        setResolving(true);
        try {
            const response = await Axios.post(
                `${process.env.REACT_APP_URL}/api/v1/hw-questions/re-solve`,
                {
                    answer_tool: chosenTool,
                    question_content: content,
                    question_type: chosenQuestionType,
                    passage: passage,
                    options: options,
                    answer_config: configByTool[chosenTool] || null,
                },
                { headers: { Authorization: getAuthHeader() } }
            );

            const { answer = [], answer_config: returnedConfig } = response.data;
            const firstAnswer = answer[0];

            // Helper: safely parse a JSON string answer
            const parseJson = (str, fallback) => {
                if (typeof str !== 'string') return fallback;
                try { return JSON.parse(str); } catch { return fallback; }
            };

            // Dispatch into the right state setter for the chosen tool. Each
            // branch mirrors how the modal initializes that tool's state from
            // question.answer / question.answer_config (lines 79-188).
            switch (chosenTool) {
                case 'coordinate':
                    if (returnedConfig) setCoordinateConfig({ ...coordinateConfig, ...returnedConfig });
                    setCoordinateAnswer(parseJson(firstAnswer, { elements: [] }));
                    break;
                case 'mapping':
                    if (returnedConfig) setMappingConfig({ ...mappingConfig, ...returnedConfig });
                    if (typeof firstAnswer === 'string') setMappingAnswer(firstAnswer);
                    break;
                case 'table': {
                    let nextConfig = returnedConfig
                        ? { ...tableConfig, ...returnedConfig }
                        : tableConfig;
                    const parsedTable = parseJson(firstAnswer, { rows: [] });
                    // Grow config.rows so the TableAdmin merge effect has a slot
                    // for every row in the AI answer (it iterates config.rows and
                    // would otherwise silently drop the trailing rows).
                    const aiRowCount = Array.isArray(parsedTable.rows) ? parsedTable.rows.length : 0;
                    const cfgRowCount = nextConfig.rows?.length || 0;
                    if (aiRowCount > cfgRowCount) {
                        const extraRows = [];
                        for (let i = 0; i < aiRowCount - cfgRowCount; i++) {
                            // createEmptyRow takes columns and returns a row whose
                            // cells dict is keyed by the current column ids.
                            extraRows.push(createEmptyRow(nextConfig.columns || []));
                        }
                        nextConfig = { ...nextConfig, rows: [...(nextConfig.rows || []), ...extraRows] };
                    }
                    setTableConfig(nextConfig);
                    setTableAnswer(parsedTable);
                    break;
                }
                case 'numberline':
                    if (returnedConfig) setNumberLineConfig({ ...numberLineConfig, ...returnedConfig });
                    setNumberLineAnswer(parseJson(firstAnswer, { elements: [] }));
                    break;
                case 'polynomialGraph':
                    if (returnedConfig) setPolynomialGraphConfig({ ...polynomialGraphConfig, ...returnedConfig });
                    setPolynomialGraphAnswer(parseJson(firstAnswer, { correctGraphIndex: 0 }));
                    break;
                case 'rationalGraph':
                    if (returnedConfig) setRationalGraphConfig({ ...rationalGraphConfig, ...returnedConfig });
                    setRationalGraphAnswer(parseJson(firstAnswer, { correctGraphIndex: 0 }));
                    break;
                case 'trigGraph':
                    if (returnedConfig) setTrigGraphConfig({ ...trigGraphConfig, ...returnedConfig });
                    setTrigGraphAnswer(parseJson(firstAnswer, { correctGraphIndex: 0 }));
                    break;
                case 'multiline':
                    if (returnedConfig) setMultilineConfig({ ...multilineConfig, ...returnedConfig });
                    setMultilineAnswer(parseJson(firstAnswer, { rows: [] }));
                    break;
                case 'mathlive':
                    if (returnedConfig) {
                        setMathliveConfig({
                            ...mathliveConfig,
                            ...returnedConfig,
                            spec: mathliveConfig.spec || null,
                        });
                    }
                    setAnswers(answer);
                    setNumAnswers(Math.max(answer.length, 1));
                    break;
                case 'textfield':
                case 'tinymce':
                default:
                    // Plain text/LaTeX/HTML answers populate the answers array.
                    setAnswers(answer);
                    setNumAnswers(Math.max(answer.length, 1));
                    break;
            }

            console.log(`Re-solved as ${chosenTool}:`, answer);
            setResolveKey(k => k + 1);
            setSuccessAlertMessage(`Question re-solved as ${chosenTool}!`);
            setSuccessAlertOpen(true);

        } catch (error) {
            console.error('Re-solve failed:', error);
            setErrorAlertMessage('Failed to re-solve: ' + (error.response?.data?.error || error.message));
            setErrorAlertOpen(true);
        } finally {
            setResolving(false);
        }
    }

    const questionTypes = [
        { label: "Multiple Choice", value: "multiple_choice" },
        // { label: "True/False", value: "true_false" },
        { label: "Short Answer", value: "short_answer" },
        { label: "Long Answer", value: "long_answer" },
        { label: "File Upload", value: "file_upload" },
        // { label: "Essay", value: "essay" },
    ];

    return (
        <Box
            sx={{
                display: 'flex',
                gap: '10px',
                alignItems: 'center',
            }}
        >
            <Button
                variant='contained'
                color='primary'
                onClick={() => {handleOpen()}}
            >
                Edit
            </Button>
            <Modal
                open={open}
                onClose={handleClose}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={style}>
                    {/* Passage Field */}
                    <Box mt={2}>
                        <TextFieldWithMath
                            label="Passage (Optional)"
                            value={passage}
                            onChange={(value) => setPassage(value)}
                            multiline
                            rows={3}
                            helperText="Shared context or reference text for this question"
                            fullWidth
                        />
                        {/* Preview for passage */}
                        {passage && (
                            <Box
                                sx={{
                                    mt: 1,
                                    p: 1.5,
                                    backgroundColor: colors.primary[600],
                                    borderRadius: '4px',
                                    border: `1px solid ${colors.grey[600]}`,
                                }}
                            >
                                <Typography variant="caption" color="textSecondary" display="block" mb={0.5}>
                                    Preview:
                                </Typography>
                                <QuestionRender content={passage} />
                            </Box>
                        )}
                    </Box>

                    {/* Question Content Field */}
                    <Box mt={2}>
                        <TextFieldWithMath
                            label="Question Content"
                            value={content}
                            onChange={(value) => setContent(value)}
                            multiline
                            rows={4}
                            fullWidth
                        />
                        {/* Preview for content */}
                        {content && (
                            <Box
                                sx={{
                                    mt: 1,
                                    p: 1.5,
                                    backgroundColor: colors.primary[600],
                                    borderRadius: '4px',
                                    border: `1px solid ${colors.grey[600]}`,
                                }}
                            >
                                <Typography variant="caption" color="textSecondary" display="block" mb={0.5}>
                                    Preview:
                                </Typography>
                                <QuestionRender content={content} />
                            </Box>
                        )}
                    </Box>

                    <Box mt={2} sx={{ display: "flex", gap: "20px", justifyContent: "flex-start", alignItems: "flex-start", flexWrap: "wrap" }}>
                        <TextField
                            variant="filled"
                            type="text"
                            label={"Question Label"}
                            onChange={(e) => {
                                setQuestionLabel(e.target.value);
                            }}
                            name={"question-label"}
                            value={questionLabel}
                            sx={{
                                minWidth: "300px",
                                maxWidth: "400px"
                            }}
                        />
                        <TextField
                            variant="filled"
                            type="number"
                            label={"Page"}
                            onChange={(e) => {
                                const val = parseInt(e.target.value, 10);
                                if (!isNaN(val)) {
                                    setNewPage(val);
                                }
                            }}
                            onBlur={() => {
                                // Validate on blur - clamp to valid range
                                if (newPage < 1) setNewPage(1);
                                if (numPages && newPage > numPages) setNewPage(numPages);
                            }}
                            name={"page"}
                            value={newPage}
                            inputProps={{
                                min: 1,
                                max: numPages || undefined,
                            }}
                            sx={{
                                width: "100px",
                            }}
                        />
                        <Autocomplete
                            disablePortal
                            id="combo-box-demo"
                            options={questionTypes}
                            sx={{
                                minWidth: "300px",
                                maxWidth: "400px"
                            }}
                            renderInput={(params) => <TextField {...params} label="Question Type" />}
                            isOptionEqualToValue={(option, value) => {
                                return option.value === value.value;
                            }}
                            onChange={(_, newValue) => {
                                setAnswers([]);
                                setOptions([]);
                                // Reset answer tool when switching types so a stale
                                // tool (e.g. numberline) doesn't hijack the save path
                                // and overwrite the new answer with its JSON blob.
                                setChosenTool(null);
                                setChosenQuestionType(newValue?.value);
                            }}
                            value={questionTypes.find((type) => type.value === chosenQuestionType)}
                        />
                    </Box>
                    <Box mt={2} sx={{ display: "flex", gap: "20px", justifyContent: "flex-start", alignItems: "flex-start", flexDirection: "column" }}>
                        {chosenQuestionType === "multiple_choice" && ( <>
                            <Typography variant="h6" fontWeight="bold">
                                Options (ex: A., B., C., D. or 1., 2., 3., 4., 1), 2), etc.)
                            </Typography>
                            <Box display='flex' justifyContent='start' alignItems='flex-start' flexDirection='column' gap={2} width="100%">
                                {options.map((option, index) => {
                                    return (
                                        <Box key={index} display="flex" gap={2} alignItems="flex-start" width="100%">
                                            {/* Preview for option */}
                                            {option && (
                                                <Box
                                                    sx={{
                                                        minWidth: '200px',
                                                        maxWidth: '200px',
                                                        p: 1.5,
                                                        backgroundColor: colors.primary[600],
                                                        borderRadius: '4px',
                                                        border: `1px solid ${colors.grey[600]}`,
                                                    }}
                                                >
                                                    <Typography variant="caption" color="textSecondary" display="block" mb={0.5}>
                                                        Preview:
                                                    </Typography>
                                                    <QuestionRender content={option} />
                                                </Box>
                                            )}
                                            <Box sx={{ flex: 1 }}>
                                                <TextFieldWithMath
                                                    label={`Option ${index + 1}`}
                                                    value={option}
                                                    onChange={(value) => {
                                                        const oldOption = option;
                                                        setOptions(options.map((opt, i) => i === index ? value : opt));
                                                        // Update answers: replace old option value with new one
                                                        setAnswers(answers.map(ans => ans === oldOption ? value : ans));
                                                    }}
                                                    multiline
                                                    rows={2}
                                                />
                                            </Box>
                                            <IconButton
                                                color="error"
                                                onClick={() => {
                                                    const deletedOption = option;
                                                    setOptions(options.filter((_, i) => i !== index));
                                                    // Remove only the deleted option from answers
                                                    setAnswers(answers.filter(ans => ans !== deletedOption));
                                                }}
                                                size="small"
                                                sx={{ mt: 1 }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    )
                                })}
                            </Box>
                            <Box display='flex' justifyContent='start' alignItems='center' flexDirection='column' gap={2}>
                                <Button type="button" color="primary" variant="contained" onClick={() => {setOptions([...options, ''])}}>Add Option</Button>
                            </Box>
                        </>)}
                    </Box>
                    {chosenQuestionType !== null && (<>
                        <Box mt={4} sx={{ display: "flex", gap: "20px", justifyContent: "flex-start", alignItems: "flex-start", flexDirection: "column", width: "100%" }}>
                            {chosenQuestionType === "multiple_choice" ? (
                                (() => {
                                    // Detect if current answers are index-based (numbers/numeric strings) or text-based
                                    const isIndexBased = answers.length > 0 &&
                                        answers.every(a => !isNaN(parseInt(a, 10)) && String(parseInt(a, 10)) === String(a));

                                    // Helper to check if option is selected
                                    const isSelected = (optionIndex) => {
                                        if (isIndexBased) {
                                            // Compare as strings to handle both number and string indices
                                            return answers.map(String).includes(String(optionIndex));
                                        } else {
                                            // Old format: check if option text is in answers
                                            return answers.includes(options[optionIndex]);
                                        }
                                    };

                                    return (
                                        <Box width="100%">
                                            <Typography variant="h6" fontWeight="bold" mb={2}>
                                                Select Correct Answer(s):
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary" display="block" mb={1}>
                                                {isIndexBased ? 'Index-based answers detected - selections use indices (0-based)' : 'Text-based answers detected'}
                                            </Typography>
                                            <Box>
                                                {options.map((option, optionIndex) => (
                                                    <Box
                                                        key={optionIndex}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'flex-start',
                                                            mb: 1,
                                                            p: 1,
                                                            border: `1px solid ${isSelected(optionIndex) ? colors.greenAccent[500] : colors.grey[700]}`,
                                                            borderRadius: '4px',
                                                            backgroundColor: isSelected(optionIndex) ? colors.greenAccent[900] : 'transparent',
                                                        }}
                                                    >
                                                        <Checkbox
                                                            checked={isSelected(optionIndex)}
                                                            onChange={(e) => {
                                                                // Always save as indices (new format) when editing
                                                                let indexAnswers = [];
                                                                if (isIndexBased) {
                                                                    // Normalize to numbers
                                                                    indexAnswers = answers.map(a => parseInt(a, 10));
                                                                } else {
                                                                    // Convert old text format to indices
                                                                    indexAnswers = answers
                                                                        .map(textAns => options.indexOf(textAns))
                                                                        .filter(idx => idx !== -1);
                                                                }

                                                                const newAnswers = e.target.checked
                                                                    ? [...indexAnswers, optionIndex]
                                                                    : indexAnswers.filter(a => a !== optionIndex);
                                                                setAnswers(newAnswers);
                                                            }}
                                                            sx={{ pt: 0 }}
                                                        />
                                                        <Box sx={{ flex: 1, pt: 0.5 }}>
                                                            <Typography variant="caption" color="textSecondary" sx={{ mr: 1 }}>
                                                                [{optionIndex}]
                                                            </Typography>
                                                            <QuestionRender content={option} />
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Box>

                                    {/* Orphaned/Custom Answers Section */}
                                    {(() => {
                                        // For index-based answers, check if index is valid; for text-based, check if text exists in options
                                        const orphanedAnswers = answers.filter(a => {
                                            if (isIndexBased) {
                                                const idx = parseInt(a, 10);
                                                return isNaN(idx) || idx < 0 || idx >= options.length;
                                            }
                                            return !options.includes(a);
                                        });
                                        if (orphanedAnswers.length > 0 || showCustomAnswerInput) {
                                            return (
                                                <Box mt={3}>
                                                    <Typography variant="h6" fontWeight="bold" mb={1} color={colors.redAccent[500]}>
                                                        {orphanedAnswers.length > 0 ? 'Orphaned/Custom Answers' : 'Custom Answers'}:
                                                    </Typography>
                                                    {orphanedAnswers.length > 0 && (
                                                        <Typography variant="caption" color="textSecondary" display="block" mb={1}>
                                                            These answers don't match current options. Delete them or update your options.
                                                        </Typography>
                                                    )}
                                                    <Box>
                                                        {orphanedAnswers.map((answer, idx) => (
                                                            <Box
                                                                key={idx}
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'flex-start',
                                                                    mb: 1,
                                                                    p: 1,
                                                                    border: `1px solid ${colors.redAccent[500]}`,
                                                                    borderRadius: '4px',
                                                                    backgroundColor: colors.redAccent[900],
                                                                }}
                                                            >
                                                                <Box sx={{ flex: 1, pt: 0.5 }}>
                                                                    <QuestionRender content={answer} />
                                                                </Box>
                                                                <IconButton
                                                                    size="small"
                                                                    color="error"
                                                                    onClick={() => {
                                                                        setAnswers(answers.filter(a => a !== answer));
                                                                    }}
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            </Box>
                                                        ))}

                                                        {/* Custom Answer Input */}
                                                        {showCustomAnswerInput && (
                                                            <Box display="flex" gap={1} mt={1}>
                                                                <TextFieldWithMath
                                                                    label="Custom Answer"
                                                                    value={customAnswerValue}
                                                                    onChange={(value) => setCustomAnswerValue(value)}
                                                                    fullWidth
                                                                    size="small"
                                                                />
                                                                <Button
                                                                    variant="contained"
                                                                    size="small"
                                                                    onClick={() => {
                                                                        if (customAnswerValue.trim()) {
                                                                            setAnswers([...answers, customAnswerValue]);
                                                                            setCustomAnswerValue("");
                                                                            setShowCustomAnswerInput(false);
                                                                        }
                                                                    }}
                                                                >
                                                                    Add
                                                                </Button>
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    onClick={() => {
                                                                        setShowCustomAnswerInput(false);
                                                                        setCustomAnswerValue("");
                                                                    }}
                                                                >
                                                                    Cancel
                                                                </Button>
                                                            </Box>
                                                        )}
                                                    </Box>

                                                    {!showCustomAnswerInput && (
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            sx={{ mt: 1 }}
                                                            onClick={() => setShowCustomAnswerInput(true)}
                                                        >
                                                            Add Custom Answer
                                                        </Button>
                                                    )}
                                                </Box>
                                            );
                                        }
                                        return null;
                                    })()}

                                    {/* Always show "Add Custom Answer" button if not already showing input */}
                                    {(() => {
                                        // For index-based answers, check if index is valid; for text-based, check if text exists in options
                                        const orphanedAnswers = answers.filter(a => {
                                            if (isIndexBased) {
                                                const idx = parseInt(a, 10);
                                                return isNaN(idx) || idx < 0 || idx >= options.length;
                                            }
                                            return !options.includes(a);
                                        });
                                        if (!showCustomAnswerInput && orphanedAnswers.length === 0) {
                                            return (
                                                <Box mt={2}>
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={() => setShowCustomAnswerInput(true)}
                                                    >
                                                        Add Custom Answer
                                                    </Button>
                                                </Box>
                                            );
                                        }
                                        return null;
                                    })()}
                                </Box>
                                    );
                                })()
                            ) : chosenQuestionType === "short_answer" || chosenQuestionType === "long_answer" ? (
                                <>
                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                        <FormControl sx={{ minWidth: 300, maxWidth: 400 }}>
                                            <InputLabel id="demo-simple-select-label">Answer Tool</InputLabel>
                                            <Select
                                                labelId="demo-simple-select-label"
                                                id="demo-simple-select"
                                                value={chosenTool}
                                                label="Answer"
                                                onChange={(event) => {
                                                    setChosenTool(event.target.value);
                                                    setAnswers([]);
                                                    // Auto-check "Render as LaTeX" when mathlive is selected
                                                    if (event.target.value === 'mathlive') {
                                                        setRenderAsLatex(true);
                                                    }
                                                }}
                                            >
                                                <MenuItem value={""}>None</MenuItem>
                                                {ANSWER_TOOLS.map((tool) => (
                                                    <MenuItem key={tool.value} value={tool.value}>{tool.label}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        {chosenQuestionType === "long_answer" && (
                                            <FormControl sx={{ minWidth: 300, maxWidth: 400 }}>
                                                <InputLabel id="grading-type-label">Grading Type</InputLabel>
                                                <Select
                                                    labelId="grading-type-label"
                                                    id="grading-type-select"
                                                    value={gradingType || ""}
                                                    label="Grading Type"
                                                    onChange={(event) => {
                                                        setGradingType(event.target.value || null);
                                                    }}
                                                >
                                                    <MenuItem value=""><em>Manual Grading</em></MenuItem>
                                                    {GRADING_TYPES.map((gt) => (
                                                        <MenuItem key={gt.value} value={gt.value}>{gt.label}</MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        )}
                                        {/* Re-solve with AI button — uses the answer tool the admin selected above */}
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            startIcon={resolving ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
                                            onClick={handleResolveWithAI}
                                            disabled={resolving || !content || !chosenTool}
                                            sx={{ height: 56 }}
                                            title={!chosenTool ? 'Pick an answer tool first' : `Re-solve with AI as ${chosenTool}`}
                                        >
                                            {resolving ? 'Solving...' : 'AI: Re-solve'}
                                        </Button>
                                    </Box>
                                    <Box display="flex" justifyContent="start" alignItems="center" flexDirection="column" gap={2} width="100%">
                                        {chosenQuestionType === "short_answer" && chosenTool === "textfield" ? (
                                            Array(numAnswers).fill(0).map((_, index) => {
                                                const answer = answers[index] || '';
                                                return (
                                                    <Box
                                                        key={index}
                                                        sx={{
                                                            display: "flex",
                                                            gap: "10px",
                                                            alignItems: "flex-start",
                                                            width: "100%",
                                                            position: "relative",
                                                            flexDirection: "column",
                                                        }}
                                                    >
                                                        <Box sx={{ display: "flex", gap: "10px", alignItems: "flex-start", width: "100%" }}>
                                                            <TextFieldWithMath
                                                                disabled={chosenQuestionType === "long_answer" || chosenQuestionType === "essay"}
                                                                label={`Answer ${index + 1}`}
                                                                value={answer}
                                                                onChange={(value) => {
                                                                    let temp = [...answers];
                                                                    const oldValue = temp[index] || '';
                                                                    temp[index] = value;
                                                                    setAnswers(temp);

                                                                    // If user removed LaTeX delimiters, uncheck renderAsLatex
                                                                    const hadDelimiters = /\\\(|\\\)|\$/.test(oldValue);
                                                                    const hasDelimiters = /\\\(|\\\)|\$/.test(value);
                                                                    if (hadDelimiters && !hasDelimiters) {
                                                                        setRenderAsLatex(false);
                                                                    }
                                                                }}
                                                                multiline
                                                                rows={2}
                                                                sx={{ minWidth: "300px", maxWidth: "400px", flex: 1 }}
                                                            />
                                                            <IconButton
                                                                color='error'
                                                                sx={{
                                                                    marginTop: '8px',
                                                                }}
                                                                onClick={() => {
                                                                    setAnswers(answers.filter((_, i) => i !== index));
                                                                    setNumAnswers(numAnswers - 1);
                                                                }}
                                                            >
                                                                <CloseIcon />
                                                            </IconButton>
                                                        </Box>
                                                        {/* Preview for answer */}
                                                        {answer && (
                                                            <Box
                                                                sx={{
                                                                    ml: 0,
                                                                    p: 1.5,
                                                                    backgroundColor: colors.primary[600],
                                                                    borderRadius: '4px',
                                                                    border: `1px solid ${colors.grey[600]}`,
                                                                    maxWidth: "400px",
                                                                    width: "100%",
                                                                }}
                                                            >
                                                                <Typography variant="caption" color="textSecondary" display="block" mb={0.5}>
                                                                    Preview:
                                                                </Typography>
                                                                <QuestionRender content={answer} />
                                                            </Box>
                                                        )}
                                                    </Box>
                                                )
                                            })
                                        ) : chosenQuestionType === "short_answer" && chosenTool === "mathlive" ? (
                                            Array(numAnswers).fill(0).map((_, index) => {
                                                const answerType = mathliveAnswerTypes[index] === MATHLIVE_ANSWER_TYPES.TEXT
                                                    ? MATHLIVE_ANSWER_TYPES.TEXT
                                                    : MATHLIVE_ANSWER_TYPES.MATH;
                                                const removeAnswer = () => {
                                                    setAnswers(answers.filter((_, i) => i !== index));
                                                    setMathliveAnswerTypes((prev) => prev.filter((_, i) => i !== index));
                                                    setNumAnswers(numAnswers - 1);
                                                };
                                                return (
                                                    <Box
                                                        key={index}
                                                        sx={{
                                                            display: "flex",
                                                            flexDirection: "column",
                                                            gap: "10px",
                                                            alignItems: "flex-start",
                                                            width: "100%",
                                                            position: "relative",
                                                        }}
                                                    >
                                                        {mathliveConfig.allowTextInput && (
                                                            <Typography variant="caption" sx={{ color: colors.grey[300] }}>
                                                                {answerType === MATHLIVE_ANSWER_TYPES.TEXT ? 'Text answer' : 'Math answer'}
                                                            </Typography>
                                                        )}
                                                        {answerType === MATHLIVE_ANSWER_TYPES.TEXT ? (
                                                            <TextField
                                                                variant="filled"
                                                                type="text"
                                                                label='Text Answer (e.g. "no solutions")'
                                                                value={answers[index] || ''}
                                                                onChange={(e) => {
                                                                    const temp = [...answers];
                                                                    temp[index] = e.target.value;
                                                                    setAnswers(temp);
                                                                }}
                                                                sx={{ minWidth: "300px", maxWidth: "400px" }}
                                                            />
                                                        ) : (
                                                            <>
                                                                <MathFieldInput
                                                                    value={answers[index] || ''}
                                                                    onInput={evt => {
                                                                        const oldValue = answers[index] || '';
                                                                        let temp = [...answers];
                                                                        temp[index] = evt.target.value;
                                                                        setAnswers(temp);
                                                                        if (hasLatexDelimiters(oldValue) && !hasLatexDelimiters(evt.target.value)) {
                                                                            setRenderAsLatex(false);
                                                                        }
                                                                    }}
                                                                    style={{ width: "inherit", minWidth: "300px", maxWidth: "100%"}}
                                                                />
                                                                <p>Answer: {answers[index]}</p>
                                                            </>
                                                        )}
                                                        <Box
                                                            sx={{
                                                                position: 'absolute',
                                                                top: '12px',
                                                                right: -30,
                                                                color: colors.redAccent[400],
                                                                zIndex: 1000,
                                                            }}
                                                        >
                                                            <CloseIcon
                                                                color='error'
                                                                sx={{
                                                                    cursor: 'pointer',
                                                                    '&:hover': {
                                                                        color: colors.redAccent[600]
                                                                    }
                                                                }}
                                                                onClick={removeAnswer}
                                                            />
                                                        </Box>
                                                    </Box>
                                                )
                                            })
                                        ) : chosenQuestionType === "short_answer" && chosenTool === "tinymce" ? (
                                            <Box></Box>
                                        ) : chosenQuestionType === "short_answer" && chosenTool === "coordinate" ? (
                                            <Box sx={{ mt: 2, width: '100%' }}>
                                                <CoordinateGridAdmin
                                                    key={`coordinate-${resolveKey}`}
                                                    config={coordinateConfig}
                                                    onConfigChange={setCoordinateConfig}
                                                    correctAnswer={coordinateAnswer}
                                                    onAnswerChange={setCoordinateAnswer}
                                                />
                                            </Box>
                                        ) : chosenQuestionType === "short_answer" && chosenTool === "mapping" ? (
                                            <Box sx={{ mt: 2, width: '100%' }}>
                                                <MappingAdmin
                                                    key={`mapping-${resolveKey}`}
                                                    config={mappingConfig}
                                                    answer={mappingAnswer}
                                                    onConfigChange={setMappingConfig}
                                                    onAnswerChange={setMappingAnswer}
                                                />
                                            </Box>
                                        ) : chosenQuestionType === "short_answer" && chosenTool === "table" ? (
                                            <Box sx={{ mt: 2, width: '100%' }}>
                                                <TableAdmin
                                                    key={`table-${resolveKey}`}
                                                    config={tableConfig}
                                                    onConfigChange={setTableConfig}
                                                    correctAnswer={tableAnswer}
                                                    onAnswerChange={setTableAnswer}
                                                />
                                            </Box>
                                        ) : chosenQuestionType === "short_answer" && chosenTool === "numberline" ? (
                                            <Box sx={{ mt: 2, width: '100%' }}>
                                                <NumberLineAdmin
                                                    key={`numberline-${resolveKey}`}
                                                    config={numberLineConfig}
                                                    onConfigChange={setNumberLineConfig}
                                                    correctAnswer={numberLineAnswer}
                                                    onAnswerChange={setNumberLineAnswer}
                                                />
                                            </Box>
                                        ) : chosenQuestionType === "short_answer" && chosenTool === "polynomialGraph" ? (
                                            <Box sx={{ mt: 2, width: '100%' }}>
                                                <PolynomialGraphAdmin
                                                    key={`polynomialGraph-${resolveKey}`}
                                                    config={polynomialGraphConfig}
                                                    onConfigChange={setPolynomialGraphConfig}
                                                    correctAnswer={polynomialGraphAnswer}
                                                    onAnswerChange={setPolynomialGraphAnswer}
                                                />
                                            </Box>
                                        ) : chosenQuestionType === "short_answer" && chosenTool === "rationalGraph" ? (
                                            <Box sx={{ mt: 2, width: '100%' }}>
                                                <RationalGraphAdmin
                                                    key={`rationalGraph-${resolveKey}`}
                                                    config={rationalGraphConfig}
                                                    onConfigChange={setRationalGraphConfig}
                                                    correctAnswer={rationalGraphAnswer}
                                                    onAnswerChange={setRationalGraphAnswer}
                                                />
                                            </Box>
                                        ) : chosenQuestionType === "short_answer" && chosenTool === "trigGraph" ? (
                                            <Box sx={{ mt: 2, width: '100%' }}>
                                                <TrigGraphAdmin
                                                    key={`trigGraph-${resolveKey}`}
                                                    config={trigGraphConfig}
                                                    onConfigChange={setTrigGraphConfig}
                                                    correctAnswer={trigGraphAnswer}
                                                    onAnswerChange={setTrigGraphAnswer}
                                                />
                                            </Box>
                                        ) : chosenQuestionType === "short_answer" && chosenTool === "multiline" ? (
                                            <Box sx={{ mt: 2, width: '100%' }}>
                                                <MultilineAdmin
                                                    key={`multiline-${resolveKey}`}
                                                    config={multilineConfig}
                                                    onConfigChange={setMultilineConfig}
                                                    correctAnswer={multilineAnswer}
                                                    onAnswerChange={setMultilineAnswer}
                                                />
                                            </Box>
                                        ) : null }
                                    </Box>
                                </>
                            ) : null }
                        </Box>
                        <Box mt={2} sx={{ display: "flex", gap: "20px", justifyContent: "flex-start", alignItems: "center", flexWrap: "wrap" }}>
                            {chosenQuestionType === "short_answer" && chosenTool === "textfield" && (
                                <>
                                    <Button
                                        type="button"
                                        color="secondary"
                                        variant="contained"
                                        onClick={() => {
                                            setNumAnswers(numAnswers + 1);
                                            setAnswers([...answers, '']);
                                        }}
                                    >
                                        Add Answer
                                    </Button>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Checkbox
                                            checked={renderAsLatex}
                                            onChange={(e) => setRenderAsLatex(e.target.checked)}
                                            size="small"
                                        />
                                        <Typography variant="body2">Render as LaTeX</Typography>
                                    </Box>
                                </>
                            )}
                            {chosenQuestionType === "short_answer" && chosenTool === "mathlive" && (
                                <>
                                    <Button
                                        type="button"
                                        color="secondary"
                                        variant="contained"
                                        onClick={() => {
                                            setNumAnswers(numAnswers + 1);
                                            setAnswers([...answers, '']);
                                            setMathliveAnswerTypes((prev) => [...prev, MATHLIVE_ANSWER_TYPES.MATH]);
                                        }}
                                    >
                                        {mathliveConfig.allowTextInput ? 'Add Math Answer' : 'Add Answer'}
                                    </Button>
                                    {mathliveConfig.allowTextInput && (
                                        <Button
                                            type="button"
                                            color="info"
                                            variant="contained"
                                            onClick={() => {
                                                setNumAnswers(numAnswers + 1);
                                                setAnswers([...answers, '']);
                                                setMathliveAnswerTypes((prev) => [...prev, MATHLIVE_ANSWER_TYPES.TEXT]);
                                            }}
                                        >
                                            Add Text Answer
                                        </Button>
                                    )}
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Checkbox
                                            checked={renderAsLatex}
                                            onChange={(e) => setRenderAsLatex(e.target.checked)}
                                            size="small"
                                        />
                                        <Typography variant="body2">Render as LaTeX</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Checkbox
                                            checked={mathliveConfig.allowTextInput}
                                            onChange={(e) => setMathliveConfig({ ...mathliveConfig, allowTextInput: e.target.checked })}
                                            size="small"
                                        />
                                        <Typography variant="body2">
                                            Allow text answer (e.g. "no solutions", "infinitely many")
                                        </Typography>
                                    </Box>
                                    <MathAnswerSpecEditor
                                        questionContent={content}
                                        passage={passage}
                                        config={mathliveConfig}
                                        onChange={setMathliveConfig}
                                    />
                                </>
                            )}
                        </Box></>
                    )}
                    <Box mt={2} sx={{ display: "flex", gap: "10px", justifyContent: "flex-end", alignItems: "flex-end" }}>
                        <Button
                            variant='contained'
                            color='primary'
                            onClick={() => {handleSave()}}
                        >
                            Save
                        </Button>
                        <Button
                            variant='contained'
                            color='error'
                            onClick={() => {handleClose()}}
                        >
                            Cancel
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
}

export default ExerciseEditModal;
